/* ==========================================================================
   Nail drill landing page motion.

   All of it is progressive enhancement. The CSS only hides or resets
   anything once this script has set data-nd-motion="on", so with JS off
   every piece renders its finished frame.

     reveal     blocks fade, rise and unblur once, the first time they
                enter the viewport, with a self-removing scroll sweep as
                the safety net for scrolls that outrun IntersectionObserver
                delivery.

     count      stat values run up from zero, once. The final frame
                restores the exact string the merchant typed.

     gauge      the speed card counts to its value while the tick ring
                lights, once, when the card comes into view.

     wave       the silence card's waveform flattens to a line, once,
                when the card comes into view.

     scroll     the hero frame straightens from a tilt as it scrolls in,
                and the statement lights word by word as it crosses the
                viewport. Both are pure functions of scroll position, so
                they play forward and back.

   One pointer behaviour runs even under reduced motion, since it moves
   nothing: the anatomy plate, where hovering a part lights its hotspot
   and hovering a hotspot lights its part. Cards and photos do nothing on
   hover (no spotlight, no zoom) since 2026-10-09.

   Everything re-inits on shopify:section:load so the theme editor stays
   in sync.
   ========================================================================== */

(function () {
  'use strict';

  var SELECTOR = '.nail-drill';
  var REVEAL_STEP = 80;
  var COUNT_MS = 1400;
  var GAUGE_MS = 1900;
  var WAVE_MS = 1700;
  var HERO_TILT = 14;
  var HERO_ZOOM = 0.06;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function formatLike(value) {
    return Math.round(value).toLocaleString(document.documentElement.lang || undefined);
  }

  /* Runs render(p) with p from 0 to 1 over duration ms, then render(1). */
  function play(duration, render) {
    var start = null;

    function tick(now) {
      if (start === null) start = now;

      var p = Math.min((now - start) / duration, 1);
      render(p);

      if (p < 1) window.requestAnimationFrame(tick);
    }

    window.requestAnimationFrame(tick);
  }

  /* Calls callback(el) once per element, the first time it is in view. */
  function onceInView(elements, threshold, callback) {
    if (!elements.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          callback(entry.target);
        });
      },
      { threshold: threshold }
    );

    Array.prototype.forEach.call(elements, function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Reveal
     ------------------------------------------------------------------------ */

  function reveal(el, observer) {
    if (el.classList.contains('is-in')) return;

    var siblings = el.parentElement
      ? Array.prototype.filter.call(el.parentElement.children, function (child) {
          return child.classList.contains('nd-reveal');
        })
      : [el];

    el.style.setProperty('--nd-delay', Math.max(siblings.indexOf(el), 0) * REVEAL_STEP + 'ms');
    el.classList.add('is-in');

    if (observer) observer.unobserve(el);
  }

  function observeReveals(root) {
    var items = root.querySelectorAll('.nd-reveal');
    if (!items.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reveal(entry.target, observer);
        });
      },
      // A fixed offset rather than a percentage: on a very tall viewport a
      // percentage can shrink the root past the last block, which would
      // leave it hidden for good.
      { rootMargin: '0px 0px -60px 0px', threshold: 0.01 }
    );

    items.forEach(function (item) {
      observer.observe(item);
    });

    /* Safety net. A fast scroll can outrun IntersectionObserver delivery:
       an element that enters and leaves the viewport between two callbacks
       is never reported, and would stay at opacity zero for good. This
       sweep reveals anything at or above the fold, including content
       already scrolled past, and unhooks itself once every item is done. */
    var pending = Array.prototype.slice.call(items);
    var queued = false;

    function sweep() {
      queued = false;

      pending = pending.filter(function (item) {
        if (item.classList.contains('is-in')) return false;

        if (item.getBoundingClientRect().top < window.innerHeight) {
          reveal(item, observer);
          return false;
        }
        return true;
      });

      if (!pending.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    }

    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(sweep);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', onScroll);
    onScroll();
  }

  /* ------------------------------------------------------------------------
     Stat count up, one shot
     ------------------------------------------------------------------------ */

  function runCount(el) {
    if (el.dataset.ndCounted === 'true') return;
    el.dataset.ndCounted = 'true';

    var finalText = el.textContent;
    /* The value may be written the locale's way: 2.35 or 2,35 for a
       decimal, 45,000, 45.000 or 45 000 for thousands. One or two digits
       after the last mark are decimals; three are a thousands group. */
    var digits = finalText.replace(/[^\d.,]/g, '');
    var fraction = digits.match(/[.,](\d{1,2})$/);
    var decimals = fraction ? fraction[1].length : 0;
    var whole = fraction ? digits.slice(0, -fraction[0].length) : digits;
    var target = parseFloat(whole.replace(/[.,]/g, '') + (fraction ? '.' + fraction[1] : ''));

    if (!isFinite(target) || target <= 0) return;

    var locale = document.documentElement.lang || undefined;

    play(COUNT_MS, function (p) {
      el.textContent =
        p < 1
          ? (target * easeOutExpo(p)).toLocaleString(locale, {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals
            })
          : finalText;
    });
  }

  /* ------------------------------------------------------------------------
     Speed gauge and silence wave
     ------------------------------------------------------------------------ */

  function initGauges(root) {
    var cards = root.querySelectorAll('[data-nd-dial]');

    Array.prototype.forEach.call(cards, function (card) {
      var number = card.querySelector('[data-nd-dial-number]');
      var ring = card.querySelector('[data-nd-dial-ring]');
      var finalText = number ? number.textContent : '';
      var target = parseFloat(card.getAttribute('data-nd-dial')) || 0;
      var circumference = ring ? 2 * Math.PI * (parseFloat(ring.getAttribute('r')) || 44) : 0;

      if (ring) {
        ring.style.strokeDasharray = String(circumference);
        ring.style.strokeDashoffset = String(circumference);
      }
      if (number && target > 0) number.textContent = formatLike(0);

      card.ndPlay = function () {
        play(GAUGE_MS, function (p) {
          var eased = easeOutExpo(p);
          if (ring) ring.style.strokeDashoffset = String(circumference * (1 - eased));
          if (number && target > 0) number.textContent = p < 1 ? formatLike(target * eased) : finalText;
        });
      };
    });

    onceInView(cards, 0.45, function (card) {
      card.ndPlay();
    });
  }

  function initWaves(root) {
    var cards = root.querySelectorAll('[data-nd-wave]');

    onceInView(cards, 0.5, function (card) {
      var form = card.querySelector('[data-nd-wave-form]');
      if (!form) return;

      // A beat at full volume before it settles, so the eye catches it.
      window.setTimeout(function () {
        play(WAVE_MS, function (p) {
          // The waveform never quite reaches zero; the residual 4 percent
          // is the one pixel line it resolves into.
          form.style.transform = 'scaleY(' + (1 - 0.96 * easeOutCubic(p)) + ')';
        });
      }, 350);
    });
  }

  /* ------------------------------------------------------------------------
     Scroll linked: hero tilt and statement words
     ------------------------------------------------------------------------ */

  /* Wraps every word of the line in a span, keeping the spaces as text.
     A language written without spaces becomes one word and lights at once. */
  function splitWords(line) {
    var words = [];

    Array.prototype.slice.call(line.childNodes).forEach(function (node) {
      if (node.nodeType !== 3) return;

      var fragment = document.createDocumentFragment();

      node.textContent.split(/(\s+)/).forEach(function (part) {
        if (!part) return;

        if (/^\s+$/.test(part)) {
          fragment.appendChild(document.createTextNode(part));
        } else {
          var span = document.createElement('span');
          span.className = 'nd-word';
          span.textContent = part;
          fragment.appendChild(span);
          words.push(span);
        }
      });

      line.replaceChild(fragment, node);
    });

    return words;
  }

  function initScroll(root) {
    var frames = Array.prototype.slice.call(root.querySelectorAll('[data-nd-tilt]'));
    var lines = Array.prototype.map.call(root.querySelectorAll('[data-nd-words]'), function (line) {
      return { el: line, words: splitWords(line), lit: -1 };
    });

    if (!frames.length && !lines.length) return;

    var ticking = false;

    function frame() {
      ticking = false;

      var viewH = window.innerHeight;

      frames.forEach(function (el) {
        // Flat once its top edge reaches a fifth of the way down the view.
        var top = el.getBoundingClientRect().top;
        var p = clamp((viewH - top) / (viewH * 0.8), 0, 1);
        var e = easeOutCubic(p);

        el.style.setProperty('--nd-tilt', (HERO_TILT * (1 - e)).toFixed(2) + 'deg');
        el.style.setProperty('--nd-zoom', (1 - HERO_ZOOM * (1 - e)).toFixed(4));
      });

      lines.forEach(function (line) {
        // Lights from the line entering the lower fifth to it reaching
        // the middle of the view.
        var rect = line.el.getBoundingClientRect();
        var mid = rect.top + rect.height / 2;
        var p = clamp((viewH * 0.85 - mid) / (viewH * 0.35), 0, 1);
        var lit = Math.round(p * line.words.length);

        if (lit === line.lit) return;
        line.lit = lit;

        line.words.forEach(function (word, i) {
          word.classList.toggle('is-lit', i < lit);
        });
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(frame);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    frame();
  }

  /* ------------------------------------------------------------------------
     Pointer: anatomy
     ------------------------------------------------------------------------ */

  function initAnatomy(root) {
    root.querySelectorAll('[data-nd-anatomy]').forEach(function (plate) {
      var parts = plate.querySelectorAll('[data-nd-part]');
      var spots = plate.querySelectorAll('[data-nd-spot]');
      if (!parts.length || !spots.length) return;

      var active = null;

      function light(n) {
        active = n;
        plate.classList.toggle('has-active', n !== null);
        parts.forEach(function (part) {
          part.classList.toggle('is-active', part.getAttribute('data-nd-part') === n);
        });
        spots.forEach(function (spot) {
          spot.classList.toggle('is-active', spot.getAttribute('data-nd-spot') === n);
        });
      }

      /* A mouse lights on hover; a finger has no hover, so a tap toggles. */
      function bind(el, n) {
        el.addEventListener('pointerenter', function (event) {
          if (event.pointerType === 'mouse') light(n);
        });
        el.addEventListener('pointerleave', function (event) {
          if (event.pointerType === 'mouse') light(null);
        });
        el.addEventListener('pointerup', function (event) {
          if (event.pointerType !== 'mouse') light(active === n ? null : n);
        });
      }

      parts.forEach(function (part) {
        bind(part, part.getAttribute('data-nd-part'));
      });
      spots.forEach(function (spot) {
        bind(spot, spot.getAttribute('data-nd-spot'));
      });
    });
  }

  /* ------------------------------------------------------------------------
     Init
     ------------------------------------------------------------------------ */

  function init(scope) {
    var roots = (scope || document).querySelectorAll(SELECTOR);

    roots.forEach(function (root) {
      if (root.dataset.ndReady === 'true') return;
      root.dataset.ndReady = 'true';

      initAnatomy(root);

      if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

      root.setAttribute('data-nd-motion', 'on');

      observeReveals(root);
      onceInView(root.querySelectorAll('[data-nd-count]'), 0.5, runCount);
      initGauges(root);
      initWaves(root);
      initScroll(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
